import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';
import { Field, TextField } from './field';

/**
 * Galeria — rota `/gallery` (álbuns) e `/gallery?album=<id>` (fotos), dentro do layout com sidebar
 * (item "Galeria", ícone `images`, grupo "Conteúdo" entre Cadastros e Configurações).
 * Prints: 14-galeria-albuns, 15-galeria-album, 16-galeria-selecao, 17-galeria-lightbox
 * (skill `aliste-se-admin-kit`). O topo é o `AppTopbar` (pílulas via `action`, voltar via `onBack`).
 *
 * Teclado: Esc fecha (na ordem: confirmação → formulário → lightbox → modo seleção); ← → navegam no lightbox;
 * Enter salva o formulário e confirma a exclusão.
 * Animações: `anim-ci-in` (dialogs, barra de seleção), `anim-gl-fade` (lightbox, overlay de arrastar),
 * `anim-gl-bar` (progresso de envio) — ver styles/globals.css.
 */

export type GalleryPhoto = {
  id: string;
  url: string;
  title?: string | null;
  createdAt: string | Date;
};

export type GalleryAlbum = {
  id: string;
  title: string;
  description?: string | null;
  coverImage?: string | null;
  /** Aparece na galeria do site (`/galeria`). */
  isPublic?: boolean;
  createdAt: string | Date;
  /** Total de fotos — vem de GET /albums (`extras` do repository). */
  photosCount?: number;
  /** Até 3 URLs para o mosaico do card (a capa primeiro). Sem isso o card usa só `coverImage`. */
  previewUrls?: string[];
};

const MES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
export const formatGalleryDate = (d: string | Date) => {
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, '0')} ${MES[x.getMonth()]} ${x.getFullYear()}`;
};
export const pad2 = (n: number) => String(n).padStart(2, '0');
export const plural = (n: number, s: string, p: string) => `${n} ${n === 1 ? s : p}`;

/* ─────────────── Topo ─────────────── */

/**
 * Pílula secundária do topo. `active` = modo seleção ligado → inverte para branco. No celular fica
 * só o ícone, pra não espremer o título do `AdminTopbar` (altura fixa de 76px).
 */
export const TopbarPill: React.FC<{
  icon: React.ComponentProps<typeof Icon>['name'];
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ icon, active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={typeof children === 'string' ? children : undefined}
    className={cn(
      'flex h-10 shrink-0 items-center justify-center gap-2 rounded-full border text-[13px] font-bold transition-colors duration-100 max-sm:w-10 sm:px-4',
      active
        ? 'border-white bg-white text-background'
        : 'border-border text-muted-foreground hover:border-ring hover:text-white',
    )}
  >
    <Icon name={icon} className="size-[15px]" />
    <span className="max-sm:sr-only">{children}</span>
  </button>
);

export const PrimaryPill: React.FC<{
  icon?: React.ComponentProps<typeof Icon>['name'];
  onClick: () => void;
  children: React.ReactNode;
}> = ({ icon = 'plus', onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={typeof children === 'string' ? children : undefined}
    className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-sand text-[13px] font-bold text-on-sand transition-[transform,background-color] duration-100 hover:bg-sand/90 active:scale-[.97] max-sm:w-10 sm:px-[18px]"
  >
    <Icon name={icon} className="size-4" strokeWidth={2} />
    <span className="max-sm:sr-only">{children}</span>
  </button>
);

/** Ações da direita do topo (vão no `action` do `AdminTopbar`). */
export const TopbarActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex shrink-0 items-center gap-2">{children}</div>
);

type TopbarMenuItem = {
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
  onSelect: () => void;
};

/**
 * Ações secundárias do topo juntas num botão "⋯" — usado no celular, onde várias pílulas espremem
 * o título. Mesma moldura da `TopbarPill` só com ícone.
 */
export const TopbarMoreMenu: React.FC<{
  items: TopbarMenuItem[];
  /** Testemunhos (print 22) usa `ellipsis-vertical`. */
  icon?: React.ComponentProps<typeof Icon>['name'];
  className?: string;
}> = ({ items, icon = 'ellipsis', className }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="Mais ações"
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-100 hover:border-ring hover:text-white data-[state=open]:border-ring data-[state=open]:text-white',
          className,
        )}
      >
        <Icon name={icon} className="size-[18px]" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={8} className="min-w-44 rounded-xl">
      {items.map((item) => (
        <DropdownMenuItem
          key={item.label}
          onSelect={item.onSelect}
          className="gap-2.5 rounded-lg py-2.5 text-[13px] font-semibold"
        >
          <Icon name={item.icon} className="size-[15px]" />
          {item.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/* ─────────────── Álbuns ─────────────── */

/** 10737418240 → "10 GB"; 1288490188 → "1,2 GB"; abaixo de 1 GB em MB. */
export const formatBytes = (bytes: number) => {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) {
    return `${gb.toLocaleString('pt-BR', { maximumFractionDigits: gb >= 100 ? 0 : 1 })} GB`;
  }
  return `${Math.round(bytes / 1024 ** 2).toLocaleString('pt-BR')} MB`;
};

export type GalleryStorageInfo = { total: number; used: number; storage: number };

export const GalleryIntro: React.FC<{
  albums: number;
  photos: number;
  /** Uso do disco do servidor. */
  storage?: GalleryStorageInfo;
}> = ({ albums, photos, storage }) => (
  <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
    <div className="mr-auto flex flex-col gap-2">
      <span className="col-label flex items-center gap-2 tracking-[.18em] text-sand-dim">
        <span className="h-px w-[18px] bg-sand-dim" />
        Acervo de fotos
      </span>
      <span className="font-display text-[clamp(26px,2.6vw,36px)] font-extrabold leading-[1.05] tracking-[-.03em] text-white">
        Registros das operações
      </span>
      <span className="max-w-[600px] text-sm text-pretty text-muted-foreground">
        Só os álbuns marcados como públicos aparecem na galeria do site. A capa é a
        primeira imagem que o visitante vê.
      </span>
    </div>
    <div className="flex rounded-[14px] border border-border bg-card">
      <span className="flex flex-col gap-1.5 px-5 py-3.5">
        <span className="col-label text-muted-foreground">Álbuns</span>
        <span className="font-display text-[26px] font-extrabold leading-none tracking-[-.03em] text-white">
          {pad2(albums)}
        </span>
      </span>
      <span className="flex flex-col gap-1.5 border-l border-border px-5 py-3.5">
        <span className="col-label text-muted-foreground">Fotos</span>
        <span className="font-display text-[26px] font-extrabold leading-none tracking-[-.03em] text-sand">
          {photos}
        </span>
      </span>
      {storage && storage.total > 0 && <StorageStat storage={storage} />}
    </div>
  </div>
);

/** "Disco 10 GB / 100 GB" + barra. Amarela a partir de 80%, vermelha a partir de 90%. */
const StorageStat: React.FC<{ storage: GalleryStorageInfo }> = ({ storage }) => {
  const pct = Math.min(100, (storage.used / storage.total) * 100);
  return (
    <span
      className="flex min-w-[170px] flex-col gap-1.5 border-l border-border px-5 py-3.5"
      title={`Arquivos do painel (fotos, vídeos, PDFs): ${formatBytes(storage.storage)}. Livre: ${formatBytes(storage.total - storage.used)}.`}
    >
      <span className="col-label text-muted-foreground">Disco</span>
      <span className="flex items-baseline gap-1.5 leading-none">
        <span className="font-display text-[26px] font-extrabold tracking-[-.03em] text-white">
          {formatBytes(storage.used)}
        </span>
        <span className="text-[12.5px] text-muted-foreground">/ {formatBytes(storage.total)}</span>
      </span>
      <span className="h-1 w-full overflow-hidden rounded-full bg-sunken">
        <span
          className={cn(
            'block h-full rounded-full',
            pct >= 90 ? 'bg-destructive' : pct >= 80 ? 'bg-sand' : 'bg-olive-icon',
          )}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
};

export const GalleryGrid: React.FC<{
  min?: number;
  gap?: 'sm' | 'lg';
  children: React.ReactNode;
}> = ({ min = 330, gap = 'lg', children }) => (
  <div
    className={cn('grid', gap === 'lg' ? 'gap-5' : 'gap-2.5')}
    style={{ gridTemplateColumns: `repeat(auto-fill,minmax(min(100%,${min}px),1fr))` }}
  >
    {children}
  </div>
);

const SelectDot: React.FC<{ selected: boolean; className?: string }> = ({
  selected,
  className,
}) => (
  <span
    className={cn(
      'absolute flex size-[30px] items-center justify-center rounded-full border-[1.5px] text-on-sand',
      selected ? 'border-sand bg-sand' : 'border-white/70 bg-sunken/50',
      className,
    )}
  >
    {selected && <Icon name="check" className="size-[15px]" strokeWidth={2.6} />}
  </span>
);

const GlassIconButton: React.FC<{
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
  danger?: boolean;
  onClick: (e: React.MouseEvent) => void;
  size?: 'sm' | 'md';
}> = ({ icon, label, danger, onClick, size = 'md' }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
    className={cn(
      'flex items-center justify-center border border-border bg-sunken/80 text-foreground backdrop-blur-md transition-colors',
      size === 'md' ? 'size-[34px] rounded-[10px]' : 'size-[30px] rounded-[9px]',
      danger
        ? 'hover:border-destructive/50 hover:text-destructive'
        : 'hover:border-ring hover:text-sand',
    )}
  >
    <Icon name={icon} className="size-[15px]" />
  </button>
);

/** "No site" (oliva) quando o álbum aparece na galeria pública; "Só no painel" (apagado) quando não. */
export const VisibilityChip: React.FC<{ isPublic: boolean }> = ({ isPublic }) => (
  <span
    className={cn(
      'col-label flex h-[26px] items-center gap-[7px] rounded-full border px-2.5 backdrop-blur-md',
      isPublic
        ? 'border-olive-line/40 bg-accent/80 text-olive-ink'
        : 'border-border bg-sunken/70 text-muted-foreground',
    )}
  >
    <Icon name={isPublic ? 'globe' : 'eye-off'} className="size-3" />
    {isPublic ? 'No site' : 'Só no painel'}
  </span>
);

export const AlbumCard: React.FC<{
  album: GalleryAlbum;
  selectMode: boolean;
  selected: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ album: a, selectMode, selected, onOpen, onEdit, onDelete }) => {
  const urls = a.previewUrls?.length ? a.previewUrls : a.coverImage ? [a.coverImage] : [];
  const n = a.photosCount ?? urls.length;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className={cn(
        'group relative flex cursor-pointer flex-col overflow-hidden rounded-[18px] border bg-card transition-[border-color,transform] duration-200 ease-[cubic-bezier(.2,.8,.2,1)] hover:-translate-y-0.5',
        selected ? 'border-sand ring-1 ring-sand' : 'border-border hover:border-ring',
      )}
    >
      <div className="relative h-[230px] bg-sunken">
        {n >= 3 && urls.length >= 3 ? (
          <div className="absolute inset-0 grid grid-cols-[2fr_1fr] grid-rows-2 gap-0.5">
            <img src={urls[0]} alt="" className="row-span-2 size-full object-cover" />
            <img src={urls[1]} alt="" className="size-full object-cover" />
            <span className="relative block overflow-hidden">
              <img src={urls[2]} alt="" className="size-full object-cover" />
              {n > 3 && (
                <span className="font-display absolute inset-0 flex items-center justify-center bg-sunken/60 text-xl font-extrabold tracking-[-.02em] text-white">
                  +{n - 3}
                </span>
              )}
            </span>
          </div>
        ) : urls[0] ? (
          <img src={urls[0]} alt="" className="absolute inset-0 size-full object-cover" />
        ) : (
          <div className="absolute inset-3.5 flex flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed border-ring text-muted-foreground">
            <Icon name="images" className="size-[26px]" />
            <span className="text-[12.5px]">Álbum sem fotos</span>
          </div>
        )}
        <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,color-mix(in_oklch,var(--surface-sunken)_55%,transparent)_0%,transparent_34%)]" />
        <span className="absolute left-3.5 top-3.5 flex flex-wrap gap-1.5">
          <span
            className={cn(
              'col-label flex h-[26px] items-center gap-[7px] rounded-full border border-border bg-sunken/70 px-2.5 backdrop-blur-md',
              n ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <Icon name="camera" className="size-3" />
            {n ? plural(n, 'foto', 'fotos') : 'Vazio'}
          </span>
          <VisibilityChip isPublic={!!a.isPublic} />
        </span>
        {selectMode ? (
          <SelectDot selected={selected} className="right-3.5 top-3.5" />
        ) : (
          <span className="absolute right-3 top-3 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <GlassIconButton icon="pencil" label="Editar" onClick={onEdit} />
            <GlassIconButton icon="trash-2" label="Excluir" danger onClick={onDelete} />
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 border-t border-border px-5 pb-5 pt-[18px]">
        <span className="font-display text-[19px] font-extrabold uppercase leading-[1.1] tracking-[-.02em] text-white">
          {a.title}
        </span>
        <span className="line-clamp-2 min-h-[39px] text-[13px] leading-normal text-muted-foreground">
          {a.description || 'Sem descrição.'}
        </span>
        <span className="mt-1 flex items-center gap-[7px] text-xs text-faint">
          <Icon name="calendar" className="size-[13px] text-olive-icon" />
          Criado em {formatGalleryDate(a.createdAt)}
        </span>
      </div>
    </div>
  );
};

export const NewAlbumCard: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-[18px] border border-dashed border-ring text-muted-foreground transition-colors hover:border-sand-dim hover:bg-sand/[.03] hover:text-sand"
  >
    <span className="flex size-[52px] items-center justify-center rounded-full border border-current">
      <Icon name="plus" className="size-[22px]" />
    </span>
    <span className="font-display text-[15px] font-extrabold uppercase tracking-[-.01em]">
      Novo álbum
    </span>
    <span className="text-[12.5px] text-faint">Um álbum por operação ou evento</span>
  </button>
);

/* ─────────────── Álbum aberto ─────────────── */

export const AlbumSummary: React.FC<{ album: GalleryAlbum; count: number }> = ({
  album: a,
  count,
}) => (
  <div className="flex flex-wrap items-stretch gap-5">
    <div className="flex min-w-0 flex-[1_1_420px] flex-col justify-center gap-2.5">
      <span className="col-label flex items-center gap-2 tracking-[.18em] text-sand-dim">
        <span className="h-px w-[18px] bg-sand-dim" />
        Criado em {formatGalleryDate(a.createdAt)}
      </span>
      <span className="max-w-[640px] text-[14.5px] leading-[1.55] text-pretty text-muted-foreground">
        {a.description || 'Sem descrição.'}
      </span>
    </div>
    <div className="flex overflow-hidden rounded-[14px] border border-border bg-card">
      <span className="flex flex-col justify-center gap-1.5 px-5 py-3.5">
        <span className="col-label text-muted-foreground">Fotos</span>
        <span className="font-display text-[26px] font-extrabold leading-none tracking-[-.03em] text-sand">
          {pad2(count)}
        </span>
      </span>
      <span className="flex flex-col justify-center gap-1.5 border-l border-border px-5 py-3.5">
        <span className="col-label text-muted-foreground">Site</span>
        <span
          className={cn(
            'flex items-center gap-2 text-[13px] font-bold',
            a.isPublic ? 'text-olive-ink' : 'text-muted-foreground',
          )}
        >
          <span
            className={cn(
              'size-[7px] rounded-full',
              a.isPublic ? 'bg-olive-icon' : 'bg-faint',
            )}
          />
          {a.isPublic ? 'Publicado' : 'Oculto'}
        </span>
      </span>
      <span className="flex items-center gap-3 border-l border-border py-3 pl-3 pr-[18px]">
        {a.coverImage ? (
          <img
            src={a.coverImage}
            alt=""
            className="h-12 w-16 rounded-lg border border-border object-cover"
          />
        ) : (
          <span className="h-12 w-16 rounded-lg border border-dashed border-ring" />
        )}
        <span className="flex flex-col gap-1">
          <span className="col-label text-muted-foreground">Capa</span>
          <span className="text-[12.5px] text-muted-foreground">
            {a.coverImage
              ? 'Passe o mouse numa foto para trocar'
              : 'Definida no primeiro envio'}
          </span>
        </span>
      </span>
    </div>
  </div>
);

/** Faixa de envio. Arrastar funciona na tela inteira (ver `useFileDrop`); a faixa é o alvo de clique. */
export const UploadStrip: React.FC<{ onPick: () => void; uploading: number }> = ({
  onPick,
  uploading,
}) => (
  <button
    type="button"
    onClick={onPick}
    className="flex items-center gap-4 rounded-2xl border border-dashed border-ring bg-sunken px-5 py-4 text-left transition-colors hover:border-sand-dim hover:bg-sand/[.03]"
  >
    <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-olive-line/40 bg-accent text-olive-icon">
      <Icon name="image-plus" className="size-5" />
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
      <span className="text-sm font-semibold text-white">
        Arraste fotos para qualquer lugar desta tela
      </span>
      <span className="text-[12.5px] text-muted-foreground">
        ou clique para escolher do computador • JPG, PNG ou WEBP • até 100 por envio
      </span>
    </span>
    {uploading > 0 && (
      <span className="col-label flex h-[30px] shrink-0 items-center gap-2 rounded-full border border-olive-line/40 bg-accent px-3 text-[10.5px] tracking-[.12em] text-olive-ink">
        Enviando {plural(uploading, 'foto', 'fotos')}
      </span>
    )}
  </button>
);

export type PhotoSort = 'recent' | 'old';

export const PhotoToolbar: React.FC<{
  sort: PhotoSort;
  onSort: (s: PhotoSort) => void;
  selectMode: boolean;
}> = ({ sort, onSort, selectMode }) => (
  <div className="flex flex-wrap items-center gap-2">
    {(['recent', 'old'] as const).map((id) => (
      <button
        key={id}
        type="button"
        onClick={() => onSort(id)}
        className={cn(
          'flex h-[34px] items-center rounded-full border px-3.5 text-[12.5px] font-bold transition-colors duration-100',
          sort === id
            ? 'border-white bg-white text-background'
            : 'border-border text-muted-foreground hover:border-ring',
        )}
      >
        {id === 'recent' ? 'Mais recentes' : 'Mais antigas'}
      </button>
    ))}
    <span className="col-label ml-auto text-[10.5px] text-faint">
      {selectMode
        ? 'Toque nas fotos para selecionar'
        : 'Clique para ampliar • ← → navega'}
    </span>
  </div>
);

export const PhotoTile: React.FC<{
  photo: GalleryPhoto;
  isCover: boolean;
  uploading?: boolean;
  selectMode: boolean;
  selected: boolean;
  onClick: () => void;
  onMakeCover: () => void;
  onDelete: () => void;
}> = ({
  photo,
  isCover,
  uploading,
  selectMode,
  selected,
  onClick,
  onMakeCover,
  onDelete,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => !uploading && onClick()}
    onKeyDown={(e) => e.key === 'Enter' && !uploading && onClick()}
    className={cn(
      'group relative aspect-[4/3] cursor-pointer overflow-hidden rounded-xl bg-sunken outline-2 outline-offset-2 transition-[outline-color] duration-100',
      selected ? 'outline outline-sand' : 'outline outline-transparent',
    )}
  >
    <img
      src={photo.url}
      alt={photo.title ?? ''}
      loading="lazy"
      className={cn(
        'size-full object-cover transition-transform duration-500 ease-[cubic-bezier(.2,.8,.2,1)] group-hover:scale-[1.04]',
        uploading && 'opacity-45',
      )}
    />
    <span
      className={cn(
        'pointer-events-none absolute inset-0',
        selected
          ? 'bg-sand/15'
          : 'bg-[linear-gradient(180deg,color-mix(in_oklch,var(--surface-sunken)_35%,transparent),transparent_40%)]',
      )}
    />
    {isCover && (
      <span className="absolute bottom-2.5 left-2.5 flex h-6 items-center rounded-[7px] bg-sand px-[9px] text-[10px] font-extrabold uppercase tracking-[.12em] text-on-sand">
        Capa
      </span>
    )}
    {uploading && (
      <span className="absolute inset-x-3 bottom-3 h-1 overflow-hidden rounded-full bg-sand/15">
        <span className="anim-gl-bar block h-full origin-left bg-sand" />
      </span>
    )}
    {selectMode && <SelectDot selected={selected} className="left-2.5 top-2.5 size-7" />}
    {!selectMode && !uploading && (
      <span className="absolute right-2 top-2 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        {!isCover && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMakeCover();
            }}
            className="flex h-[30px] items-center rounded-[9px] border border-border bg-sunken/80 px-2.5 text-[11px] font-bold text-foreground backdrop-blur-md transition-colors hover:border-ring hover:text-sand"
          >
            Usar como capa
          </button>
        )}
        <GlassIconButton
          icon="trash-2"
          label="Excluir"
          danger
          size="sm"
          onClick={onDelete}
        />
      </span>
    )}
  </div>
);

export const AlbumEmpty: React.FC<{ onPick: () => void }> = ({ onPick }) => (
  <div className="flex flex-col items-center gap-3 rounded-[18px] border border-border bg-card px-6 py-[72px] text-center">
    <span className="flex size-14 items-center justify-center rounded-full border border-dashed border-ring text-muted-foreground">
      <Icon name="images" className="size-[22px]" />
    </span>
    <span className="font-display text-[17px] font-extrabold uppercase tracking-[-.01em] text-white">
      Nenhuma foto neste álbum
    </span>
    <span className="max-w-[380px] text-[13px] text-pretty text-muted-foreground">
      A primeira foto enviada vira a capa automaticamente. Você pode trocá-la depois.
    </span>
    <button
      type="button"
      onClick={onPick}
      className="mt-1.5 flex h-11 items-center gap-2 rounded-full bg-sand px-[22px] text-[13.5px] font-bold text-on-sand hover:bg-sand/90"
    >
      Adicionar fotos
    </button>
  </div>
);

/* ─────────────── Seleção ─────────────── */

/** Ação secundária da SelectionBar (contornada). Use em `children` para ações extras (ex.: Testemunhos). */
export const SelectionAction: React.FC<{
  icon?: React.ComponentProps<typeof Icon>['name'];
  onClick: () => void;
  children: React.ReactNode;
}> = ({ icon, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-[38px] items-center gap-[7px] whitespace-nowrap rounded-full border border-border px-[13px] text-[12.5px] font-bold text-foreground hover:border-sand-dim hover:text-sand"
  >
    {icon && <Icon name={icon} className="size-3.5" />}
    {children}
  </button>
);

export const SelectionBar: React.FC<{
  label: string;
  onSelectAll?: () => void;
  /** Default "Marcar todas" (fotos). Testemunhos usa "Marcar todos". */
  selectAllLabel?: string;
  onCover?: () => void;
  onDelete: () => void;
  onCancel: () => void;
  /** Ações extras antes de "Excluir" — use `<SelectionAction>`. */
  children?: React.ReactNode;
}> = ({
  label,
  onSelectAll,
  selectAllLabel = 'Marcar todas',
  onCover,
  onDelete,
  onCancel,
  children,
}) => (
  <div className="anim-ci-in fixed inset-x-3 bottom-6 z-30 mx-auto flex w-fit max-w-[calc(100vw-24px)] flex-wrap items-center justify-center gap-2 rounded-[22px] border border-ring bg-card/95 py-2 pl-5 pr-2 shadow-[0_20px_60px_rgba(0,0,0,.7)] backdrop-blur-xl md:rounded-full">
    <span className="font-display mr-2 whitespace-nowrap text-[15px] font-extrabold tracking-[-.01em] text-sand">
      {label}
    </span>
    {onSelectAll && (
      <button
        type="button"
        onClick={onSelectAll}
        className="flex h-[38px] items-center rounded-full px-3.5 text-[12.5px] font-bold text-muted-foreground hover:bg-raised hover:text-white"
      >
        {selectAllLabel}
      </button>
    )}
    {onCover && (
      <button
        type="button"
        onClick={onCover}
        className="flex h-[38px] items-center rounded-full px-3.5 text-[12.5px] font-bold text-muted-foreground hover:bg-raised hover:text-white"
      >
        Usar como capa
      </button>
    )}
    {children}
    <button
      type="button"
      onClick={onDelete}
      className="flex h-[38px] items-center gap-[7px] rounded-full bg-destructive px-4 text-[12.5px] font-bold text-white hover:bg-destructive/90"
    >
      <Icon name="trash-2" className="size-3.5" />
      Excluir
    </button>
    <button
      type="button"
      onClick={onCancel}
      aria-label="Cancelar seleção"
      className="flex size-[38px] items-center justify-center rounded-full border border-border text-muted-foreground hover:text-white"
    >
      <Icon name="x" className="size-[15px]" />
    </button>
  </div>
);

/* ─────────────── Arrastar para enviar ─────────────── */

/** Ouve drag & drop no documento inteiro enquanto `enabled`. Retorna se há arquivo sobre a tela. */
export function useFileDrop(enabled: boolean, onFiles: (files: File[]) => void) {
  const [dragging, setDragging] = React.useState(false);
  const cb = React.useRef(onFiles);
  cb.current = onFiles;

  React.useEffect(() => {
    if (!enabled) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      setDragging(true);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const leave = () => {
      depth = Math.max(0, depth - 1);
      if (!depth) setDragging(false);
    };
    const drop = (e: DragEvent) => {
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []).filter((f) =>
        f.type.startsWith('image/'),
      );
      if (files.length) cb.current(files);
    };
    document.addEventListener('dragenter', enter);
    document.addEventListener('dragover', over);
    document.addEventListener('dragleave', leave);
    document.addEventListener('drop', drop);
    return () => {
      document.removeEventListener('dragenter', enter);
      document.removeEventListener('dragover', over);
      document.removeEventListener('dragleave', leave);
      document.removeEventListener('drop', drop);
    };
  }, [enabled]);

  return dragging;
}

export const DropOverlay: React.FC<{ show: boolean; albumTitle: string }> = ({
  show,
  albumTitle,
}) =>
  show ? (
    <div className="anim-gl-fade pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(3,4,2,.86)] p-7 backdrop-blur-[6px]">
      <div className="flex size-full flex-col items-center justify-center gap-3.5 rounded-[28px] border-2 border-dashed border-sand-dim text-center">
        <span className="flex size-[72px] items-center justify-center rounded-full border border-sand-dim text-sand">
          <Icon name="upload-cloud" className="size-[30px]" />
        </span>
        <span className="font-display text-[26px] font-extrabold uppercase tracking-[-.025em] text-white">
          Solte para enviar
        </span>
        <span className="text-sm text-muted-foreground">
          As fotos entram em <strong className="text-sand">{albumTitle}</strong>
        </span>
      </div>
    </div>
  ) : null;

/* ─────────────── Lightbox ─────────────── */

export const PhotoLightbox: React.FC<{
  photos: GalleryPhoto[];
  index: number | null;
  albumTitle: string;
  coverUrl?: string | null;
  onIndex: (i: number) => void;
  onClose: () => void;
  onCover: (p: GalleryPhoto) => void;
  onDelete: (p: GalleryPhoto) => void;
  /** Pausa ← → / Esc enquanto um dialog de confirmação estiver aberto por cima. */
  paused?: boolean;
}> = ({
  photos,
  index,
  albumTitle,
  coverUrl,
  onIndex,
  onClose,
  onCover,
  onDelete,
  paused,
}) => {
  const n = photos.length;
  const open = index != null && n > 0;
  React.useEffect(() => {
    if (!open || paused) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex((index! + 1) % n);
      if (e.key === 'ArrowLeft') onIndex((index! - 1 + n) % n);
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, paused, index, n, onClose, onIndex]);
  const p = open ? photos[index!] : undefined;
  if (!p) return null;
  const isCover = !!coverUrl && p.url === coverUrl;
  const navBtn =
    'absolute top-1/2 -mt-6 flex size-12 items-center justify-center rounded-full border border-border bg-sunken/70 text-foreground transition-colors hover:border-sand-dim hover:text-sand';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="anim-gl-fade fixed inset-0 z-50 flex flex-col bg-[rgba(3,4,2,.96)]"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2.5 px-4 py-4 md:px-7">
        <span className="font-display text-[15px] font-extrabold tracking-[-.01em] text-sand">
          {pad2(index! + 1)} / {pad2(n)}
        </span>
        <span className="mr-auto truncate text-[13px] text-muted-foreground">
          {albumTitle}
        </span>
        {isCover ? (
          <span className="flex h-[34px] items-center rounded-full bg-sand px-3 text-[10.5px] font-extrabold uppercase tracking-[.12em] text-on-sand">
            Capa do álbum
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onCover(p)}
            className="flex h-[38px] items-center rounded-full border border-border px-4 text-[12.5px] font-bold text-muted-foreground hover:border-ring hover:text-white"
          >
            Usar como capa
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(p)}
          className="flex h-[38px] items-center rounded-full border border-border px-4 text-[12.5px] font-bold text-muted-foreground hover:border-destructive/50 hover:text-destructive"
        >
          Excluir
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar (Esc)"
          className="flex size-10 items-center justify-center rounded-full border border-border text-foreground hover:border-ring hover:text-sand"
        >
          <Icon name="x" className="size-[18px]" />
        </button>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-24">
        <img
          key={p.id}
          src={p.url}
          alt={p.title ?? ''}
          className="anim-gl-fade block max-h-full max-w-full rounded-xl object-contain"
        />
        <button
          type="button"
          aria-label="Anterior"
          onClick={() => onIndex((index! - 1 + n) % n)}
          className={cn(navBtn, 'left-2 md:left-7')}
        >
          <Icon name="chevron-left" className="size-[22px]" />
        </button>
        <button
          type="button"
          aria-label="Próxima"
          onClick={() => onIndex((index! + 1) % n)}
          className={cn(navBtn, 'right-2 md:right-7')}
        >
          <Icon name="chevron-right" className="size-[22px]" />
        </button>
      </div>
      <div className="flex shrink-0 justify-[safe_center] gap-1.5 overflow-x-auto px-4 pb-5 pt-4 md:px-7">
        {photos.map((t, i) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onIndex(i)}
            className={cn(
              'shrink-0 rounded-lg outline-2 outline-offset-2 transition-opacity',
              i === index
                ? 'outline outline-sand'
                : 'opacity-40 outline outline-transparent hover:opacity-70',
            )}
          >
            <img
              src={t.url}
              alt=""
              className="h-[54px] w-[72px] rounded-lg object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─────────────── Dialogs ─────────────── */

const DialogShell: React.FC<{
  onClose: () => void;
  max: string;
  z?: string;
  children: React.ReactNode;
}> = ({ onClose, max, z = 'z-40', children }) => (
  <div
    onClick={onClose}
    className={cn(
      'fixed inset-0 flex items-center justify-center bg-[rgba(3,4,2,.78)] p-5 backdrop-blur-[6px]',
      z,
    )}
  >
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'anim-ci-in w-full rounded-3xl border border-border bg-card shadow-[0_30px_80px_rgba(0,0,0,.7)]',
        max,
      )}
    >
      {children}
    </div>
  </div>
);

/**
 * "Mostrar no site" — mesma caixa do `Field` (raio 14, bg-field, trilho oliva no foco), com o
 * interruptor à direita: ligado = trilho areia.
 */
const PublicSwitch: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({
  checked,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={cn(
      'group relative flex min-h-[66px] items-center gap-4 rounded-[14px] border bg-field px-[18px] py-[13px] text-left outline-none',
      'transition-[background-color,border-color] duration-150 focus-visible:border-olive-line focus-visible:bg-accent',
      checked ? 'border-olive-line/60' : 'border-border',
    )}
  >
    <span className="pointer-events-none absolute inset-y-[13px] left-0 w-[3px] rounded-full bg-olive-icon opacity-0 transition-opacity duration-150 group-focus-visible:opacity-100" />
    <span className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="col-label text-muted-foreground">Galeria do site</span>
      <span className="text-[15px] font-semibold text-white">
        {checked ? 'Aparece em /galeria' : 'Só no painel'}
      </span>
    </span>
    <span
      className={cn(
        'relative h-7 w-12 shrink-0 rounded-full border transition-colors duration-150',
        checked ? 'border-sand bg-sand' : 'border-border bg-sunken',
      )}
    >
      <span
        className={cn(
          'absolute top-[3px] size-5 rounded-full transition-[left,background-color] duration-150',
          checked ? 'left-[23px] bg-on-sand' : 'left-[3px] bg-muted-foreground',
        )}
      />
    </span>
  </button>
);

export type AlbumFormValues = { title: string; description: string; isPublic: boolean };

export const AlbumFormDialog: React.FC<{
  open: boolean;
  /** Com `id` = edição. */
  values?: (AlbumFormValues & { id?: string }) | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (v: AlbumFormValues) => void;
}> = ({ open, values, loading, onClose, onSubmit }) => {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isPublic, setIsPublic] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setTitle(values?.title ?? '');
      setDescription(values?.description ?? '');
      setIsPublic(values?.isPublic ?? false);
    }
  }, [open, values]);
  React.useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!open) return null;
  const editing = !!values?.id;
  const valid = !!title.trim();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (valid && !loading)
      onSubmit({ title: title.trim(), description: description.trim(), isPublic });
  };

  return (
    <DialogShell onClose={onClose} max="max-w-[520px]">
      <form onSubmit={submit}>
        <div className="flex flex-col gap-1.5 px-[26px] pt-6">
          <span className="col-label tracking-[.16em] text-sand-dim">
            {editing ? 'Editar álbum' : 'Novo álbum'}
          </span>
          <span className="font-display text-2xl font-extrabold uppercase leading-[1.05] tracking-[-.025em] text-white">
            {editing ? 'Detalhes do álbum' : 'Criar álbum'}
          </span>
        </div>
        <div className="flex flex-col gap-4 px-[26px] pb-[26px] pt-[22px]">
          <Field
            label="Título"
            required
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Aliste-se Forja 2026"
          />
          <TextField
            label="Descrição"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex: Registros dos três dias de operação em Santa Bárbara d’Oeste."
          />
          <PublicSwitch checked={isPublic} onChange={setIsPublic} />
          <span className="text-xs text-faint">
            {editing
              ? isPublic
                ? 'As alterações aparecem na galeria do site imediatamente.'
                : 'O álbum fica só no painel até ser marcado para o site.'
              : 'Depois de criar, arraste as fotos direto para o álbum.'}
          </span>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex h-[50px] flex-[1_1_140px] items-center justify-center rounded-full border border-border text-sm font-semibold text-muted-foreground hover:border-ring hover:text-white"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!valid || loading}
              className="flex h-[50px] flex-[2_1_220px] items-center justify-center gap-2 rounded-full bg-sand text-sm font-bold text-on-sand transition-transform hover:bg-sand/90 active:scale-[.97] disabled:opacity-40"
            >
              {loading
                ? 'Salvando…'
                : editing
                  ? 'Salvar alterações'
                  : 'Criar e abrir álbum'}
            </button>
          </div>
        </div>
      </form>
    </DialogShell>
  );
};

export const ConfirmDeleteDialog: React.FC<{
  open: boolean;
  title: string;
  body: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, title, body, loading, onCancel, onConfirm }) => {
  React.useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onCancel();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [open, onCancel, onConfirm]);
  if (!open) return null;
  return (
    <DialogShell onClose={onCancel} max="max-w-[440px]" z="z-[55]">
      <div className="flex flex-col gap-4 p-[26px]">
        <span className="flex size-12 items-center justify-center rounded-full border border-destructive/45 bg-destructive/10 text-destructive">
          <Icon name="triangle-alert" className="size-[22px]" />
        </span>
        <span className="flex flex-col gap-2">
          <span className="font-display text-[21px] font-extrabold uppercase leading-[1.1] tracking-[-.02em] text-white">
            {title}
          </span>
          <span className="text-[13.5px] leading-[1.55] text-pretty text-muted-foreground">
            {body}
          </span>
        </span>
        <div className="mt-1 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="flex h-12 flex-[1_1_120px] items-center justify-center rounded-full border border-border text-sm font-semibold text-muted-foreground hover:border-ring hover:text-white"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex h-12 flex-[1_1_160px] items-center justify-center rounded-full bg-destructive text-sm font-bold text-white hover:bg-destructive/90 active:scale-[.97] disabled:opacity-60"
          >
            {loading ? 'Excluindo…' : 'Excluir'}
          </button>
        </div>
      </div>
    </DialogShell>
  );
};

/* ─────────────── Importar do Instagram ─────────────── */

export type InstagramAccountRow = {
  id: string;
  username: string;
  tokenExpiresAt: string | Date;
  /** Última passada do robô (últimos posts pro site) e o erro dela. */
  lastSyncAt?: string | Date | null;
  lastSyncError?: string | null;
};

/** "Posts do site atualizados 07/10 14:05" — ou o erro da última passada do robô. */
const syncLabel = (a: InstagramAccountRow) => {
  if (a.lastSyncError) return { text: `Erro ao atualizar os posts: ${a.lastSyncError}`, error: true };
  if (!a.lastSyncAt) return { text: 'Posts do site ainda não atualizados', error: false };
  const d = new Date(a.lastSyncAt);
  const when = `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return { text: `Posts do site atualizados ${when}`, error: false };
};

/**
 * Dialog "Importar do Instagram" do álbum aberto: cola o link do post e as fotos entram no álbum
 * (carrossel inteiro; vídeo pela capa). Embaixo, as contas conectadas — o post precisa ser de uma delas.
 */
export const InstagramImportDialog: React.FC<{
  open: boolean;
  albumTitle: string;
  /** `false` = servidor sem INSTAGRAM_APP_ID/SECRET. */
  configured: boolean;
  accounts: InstagramAccountRow[];
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onImport: (url: string) => void;
  onConnect: () => void;
  onDisconnect: (account: InstagramAccountRow) => void;
  /** Importação de todos os posts antigos rodando no servidor. */
  importingHistory?: boolean;
  onImportHistory?: () => void;
}> = ({
  open,
  albumTitle,
  configured,
  accounts,
  loading,
  error,
  onClose,
  onImport,
  onConnect,
  onDisconnect,
  importingHistory,
  onImportHistory,
}) => {
  const [url, setUrl] = React.useState('');
  React.useEffect(() => {
    if (open) setUrl('');
  }, [open]);
  React.useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!open) return null;

  const ready = configured && accounts.length > 0;
  const valid = /instagram\.com\/.+/i.test(url.trim());
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (ready && valid && !loading) onImport(url.trim());
  };
  const daysLeft = (d: string | Date) =>
    Math.max(0, Math.ceil((new Date(d).getTime() - Date.now()) / 86_400_000));

  return (
    <DialogShell onClose={onClose} max="max-w-[520px]">
      <form onSubmit={submit}>
        <div className="flex flex-col gap-1.5 px-[26px] pt-6">
          <span className="col-label tracking-[.16em] text-sand-dim">Importar do Instagram</span>
          <span className="font-display text-2xl font-extrabold uppercase leading-[1.05] tracking-[-.025em] text-white">
            {albumTitle || 'Álbum'}
          </span>
        </div>
        <div className="flex flex-col gap-4 px-[26px] pb-[26px] pt-[22px]">
          {!configured ? (
            <span className="rounded-[14px] border border-dashed border-sand-dim px-4 py-3.5 text-[13px] leading-[1.55] text-muted-foreground">
              A integração com o Instagram ainda não foi configurada no servidor. Peça para
              cadastrar <strong className="text-foreground">INSTAGRAM_APP_ID</strong> e{' '}
              <strong className="text-foreground">INSTAGRAM_APP_SECRET</strong>.
            </span>
          ) : (
            <>
              <Field
                label="Link do post"
                required
                autoFocus
                disabled={!ready}
                value={url}
                invalid={!!error}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Ex: https://www.instagram.com/p/ABC123/"
                leading={<Icon name="link" className="size-4 shrink-0 text-olive-icon" />}
              />
              {error ? (
                <span className="-mt-2 text-xs text-destructive">{error}</span>
              ) : (
                <span className="-mt-2 text-xs text-faint">
                  Carrossel entra com todas as fotos; vídeo e reel entram pela capa. Fotos já
                  importadas neste álbum são puladas.
                </span>
              )}

              <div className="flex flex-col gap-2">
                <span className="col-label text-muted-foreground">Contas conectadas</span>
                {accounts.length === 0 && (
                  <span className="rounded-[14px] border border-dashed border-border px-4 py-3 text-[13px] text-muted-foreground">
                    Nenhuma conta conectada. Conecte @alisteseacamps e @alistesekids para importar
                    os posts.
                  </span>
                )}
                {accounts.map((a) => {
                  const left = daysLeft(a.tokenExpiresAt);
                  const sync = syncLabel(a);
                  return (
                    <div
                      key={a.id}
                      className="flex items-center gap-3 rounded-[14px] border border-border bg-sunken px-4 py-2.5"
                    >
                      <Icon name="instagram" className="size-4 shrink-0 text-olive-icon" />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-semibold text-white">
                          @{a.username}
                        </span>
                        <span
                          title={sync.text}
                          className={cn(
                            'truncate text-[11.5px]',
                            sync.error ? 'text-destructive' : 'text-faint',
                          )}
                        >
                          {sync.text}
                        </span>
                      </span>
                      <span
                        className={cn(
                          'shrink-0 text-[11.5px]',
                          left <= 7 ? 'text-sand' : 'text-faint',
                        )}
                      >
                        {left === 0 ? 'Expirada' : `Vale por ${plural(left, 'dia', 'dias')}`}
                      </span>
                      <button
                        type="button"
                        title="Desconectar"
                        onClick={() => onDisconnect(a)}
                        className="flex size-8 shrink-0 items-center justify-center rounded-[9px] border border-border text-muted-foreground hover:border-destructive/60 hover:text-destructive"
                      >
                        <Icon name="unplug" className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={onConnect}
                  className="flex h-11 items-center justify-center gap-2 rounded-full border border-dashed border-ring text-[13px] font-bold text-muted-foreground hover:border-sand-dim hover:text-white"
                >
                  <Icon name="plus" className="size-4" />
                  Conectar conta do Instagram
                </button>
                {accounts.length > 0 && onImportHistory && (
                  <div className="flex flex-col gap-1.5 pt-1">
                    <button
                      type="button"
                      disabled={importingHistory}
                      onClick={onImportHistory}
                      className="flex h-11 items-center justify-center gap-2 rounded-full border border-border text-[13px] font-bold text-muted-foreground hover:border-sand-dim hover:text-white disabled:pointer-events-none disabled:opacity-60"
                    >
                      <Icon
                        name={importingHistory ? 'loader-circle' : 'history'}
                        className={cn('size-4', importingHistory && 'animate-spin')}
                      />
                      {importingHistory ? 'Importando posts antigos…' : 'Importar posts antigos'}
                    </button>
                    <span className="text-center text-[11.5px] text-faint">
                      Traz todas as fotos já postadas para os álbuns "Instagram · @conta". Os posts
                      novos entram sozinhos de hora em hora.
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex h-[50px] flex-[1_1_140px] items-center justify-center rounded-full border border-border text-sm font-semibold text-muted-foreground hover:border-ring hover:text-white"
            >
              Cancelar
            </button>
            {configured && (
              <button
                type="submit"
                disabled={!ready || !valid || loading}
                className="flex h-[50px] flex-[2_1_220px] items-center justify-center gap-2 rounded-full bg-sand text-sm font-bold text-on-sand transition-transform hover:bg-sand/90 active:scale-[.97] disabled:opacity-40"
              >
                {loading ? 'Importando…' : 'Importar fotos'}
              </button>
            )}
          </div>
        </div>
      </form>
    </DialogShell>
  );
};
